import React from 'react';

import {
  DndContext,
  DragOverlay,
  DragEndEvent,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { nanoid } from '@reduxjs/toolkit';
import { useForm } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import { Box } from '@vk-hr-tek/ui/Box';
import { Link } from '@vk-hr-tek/ui/Link';
import { TextInput } from '@vk-hr-tek/app-ui/form';

import {
  ContentSurveysFormValues,
  ContentSurveysFormValuesAnswers,
} from '../../../../types';

import { AnswerContainer } from './AnswerContainer';
import { AnswerDnDFallback } from './AnswerDnDFallback';

const ANSWERS_LIMIT = 10;
const MIN_OPTIONS_TO_ENABLE_DELETION = 3;

interface OneAnswerProps {
  name: string;
}

export const OneAnswer = ({ name }: OneAnswerProps) => {
  const { mutators } = useForm<ContentSurveysFormValues>();

  const handleAddAnswer = () => {
    mutators.push(`${name}.answers`, { content: '', id: nanoid() });
  };

  const handleRemoveAnswer = (index: number) => () => {
    mutators.remove(`${name}.answers`, index);
  };

  const handleDragEnd = (
    event: DragEndEvent,
    answers: ContentSurveysFormValuesAnswers = [],
  ) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = answers.findIndex((item) => item.id === active.id);
      const newIndex = answers.findIndex((item) => item.id === over.id);

      mutators.move(`${name}.answers`, oldIndex, newIndex);
    }
  };

  return (
    <FieldArray name={`${name}.answers`}>
      {({ fields }) => {
        const fieldsAmount = fields.value?.length ?? 0;

        return (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={(event) => handleDragEnd(event, fields.value)}
          >
            <Box display="flex" flexDirection="column" gap="16">
              <SortableContext
                strategy={verticalListSortingStrategy}
                items={fields.value.map((item) => item.id)}
              >
                {fields.map((answerName, index) => (
                  <AnswerContainer
                    type="one"
                    id={fields.value[index].id}
                    key={fields.value[index].id}
                    dndFallback={<AnswerDnDFallback />}
                    onRemove={handleRemoveAnswer(index)}
                    removeActionDisabled={
                      fieldsAmount < MIN_OPTIONS_TO_ENABLE_DELETION
                    }
                  >
                    <TextInput
                      multiline
                      required
                      maxLength={300}
                      debounceTimeout={200}
                      name={`${answerName}.content`}
                      placeholder="Добавьте вариант ответа"
                      helperText="Количество символов ограничено"
                    />
                  </AnswerContainer>
                ))}
              </SortableContext>

              {fieldsAmount < ANSWERS_LIMIT && (
                <Link
                  size="small"
                  stroke={false}
                  onClick={handleAddAnswer}
                  disabled={fieldsAmount === ANSWERS_LIMIT}
                >
                  Добавить вариант ответа
                </Link>
              )}
            </Box>

            <DragOverlay dropAnimation={{ duration: 400, easing: 'ease-out' }}>
              <AnswerDnDFallback />
            </DragOverlay>
          </DndContext>
        );
      }}
    </FieldArray>
  );
};
